import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { useToast } from '../Toast/Toast';
import {
  INV_LINE_REPLIES,
  INV_NUMBERS,
  INV_PATIENT,
  INV_PROGRAMME,
  INV_PROGRAMME_FIELDS,
} from '../../invisalign/mockData';

export interface InvProgrammeViewProps {
  /** email-1 | text-1 | replies … (defaults to email-1). */
  step: string;
  onSelectStep: (step: string) => void;
  onBack: () => void;
}

type Lang = 'en' | 'es' | 'fa';
const LANGS: { id: Lang; label: string }[] = [{ id: 'en', label: 'English' }, { id: 'es', label: 'Spanish' }, { id: 'fa', label: 'Farsi' }];

/**
 * The programme (amendment 33 §1, boards INV9ProgEmail / INV10ProgText) — a
 * full-width page: the 13 messages in day order on the left, the editor in the
 * middle, a live preview on the right, and the six fixed Invis-Line replies
 * below. You write English; ELVA translates.
 */
export function InvProgrammeView({ step, onSelectStep, onBack }: InvProgrammeViewProps) {
  const { toast } = useToast();
  const selected = useMemo(() => INV_PROGRAMME.find((m) => m.id === step) ?? INV_PROGRAMME[0], [step]);
  const [lang, setLang] = useState<Lang>('en');
  const [subject, setSubject] = useState(selected.subject ?? '');
  const [body, setBody] = useState(selected.body[lang]);
  const [testTo, setTestTo] = useState('');
  const [confirmRevert, setConfirmRevert] = useState(false);

  // The preview always follows the patient's language (amendment 34 §3.6), not
  // the tab being edited. Orville is English.
  const patientLang: Lang = 'en';

  // Reset to English whenever the step changes (§3.6).
  React.useEffect(() => { setLang('en'); }, [selected]);
  // Reset the editor when the selected message or language changes.
  React.useEffect(() => { setSubject(selected.subject ?? ''); setBody(selected.body[lang]); setConfirmRevert(false); }, [selected, lang]);

  const isText = selected.kind === 'text';
  const linkMissing = !isText && !body.includes('{Consult booking link}') && !subject.includes('{Consult booking link}');
  const textHasMoney = isText && body.includes('$');
  const chars = body.length;
  const segments = Math.max(1, Math.ceil(chars / 160));
  // The preview renders the patient's language: live edits when that tab is open,
  // otherwise the saved patient-language version.
  const previewSubject = lang === patientLang ? subject : (selected.subject ?? '');
  const previewBody = lang === patientLang ? body : selected.body[patientLang];

  const save = () => {
    if (linkMissing) { toast({ title: 'Add the consult booking link before saving', variant: 'warning' }); return; }
    if (textHasMoney) { toast({ title: 'Texts can’t include an amount of money', variant: 'warning' }); return; }
    toast({ title: 'Saved — patients get the new version from their next message', variant: 'success' });
  };
  const insertField = (f: string) => { setBody((b) => `${b} {${f}}`); };

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1320px] flex-1 overflow-y-auto px-[24px] pb-16 pt-5">
      <div className="flex items-center gap-3">
        <button type="button" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onBack}>
          <Icon kind="arrow-l" size={14} stroke={2} /> Back to Settings
        </button>
        <span className="text-[11.5px] text-[var(--text-muted)]">Settings › Invisalign Coordinator › The programme</span>
      </div>
      <h1 className="m-0 mt-2 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">The <span className="text-[var(--brand-indigo)]">programme</span></h1>

      <div className="mt-4 grid grid-cols-[330px_1fr_360px] gap-4 max-[1100px]:grid-cols-1">
        {/* left — timeline */}
        <aside className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-2 shadow-[var(--shadow-card)]">
          {INV_PROGRAMME.map((m) => (
            <button key={m.id} type="button" onClick={() => { onSelectStep(m.id); }}
              className={cn('flex w-full items-start gap-2.5 rounded-[10px] px-3 py-2 text-left', m.id === selected.id ? 'bg-[var(--brand-indigo-tint)]' : 'hover:bg-[var(--surface-sunken)]')}>
              <span className="mt-0.5 flex h-6 w-9 shrink-0 items-center justify-center rounded-full font-[var(--font-mono)] text-[10.5px] font-bold text-[var(--text-muted)]" style={{ background: 'var(--surface-sunken)' }}>{m.day}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <Icon kind={m.kind === 'text' ? 'chat' : 'mail'} size={13} stroke={2} style={{ color: m.kind === 'text' ? 'var(--brand-indigo)' : 'var(--text-muted)' }} />
                  <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{m.title}</span>
                </span>
                {m.kind === 'text' ? <span className="mt-0.5 block text-[10.5px] text-[var(--text-muted)]">Text · from the Invis-Line · only with text consent</span> : null}
                {m.edited === true ? <span className="mt-0.5 block text-[10.5px] font-semibold text-[var(--brand-indigo)]">edited by you</span> : null}
              </span>
            </button>
          ))}
          <div className="mt-1 border-t border-[var(--border-default)] px-3 py-2 text-[11px] text-[var(--text-muted)]">Stops the moment a consult is booked. Patients without text consent get the emails only.</div>
        </aside>

        {/* middle — editor */}
        <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ color: isText ? 'var(--brand-indigo)' : 'var(--text-secondary)', background: 'var(--surface-sunken)' }}>{isText ? 'Text' : 'Email'} · day {selected.day}</span>
            {/* language tabs */}
            <div className="ml-auto flex gap-1">
              {LANGS.map((l) => (
                <button key={l.id} type="button" onClick={() => { setLang(l.id); }} className={cn('rounded-full px-2.5 py-1 text-[11.5px] font-semibold', l.id === lang ? 'bg-[var(--brand-indigo)] text-white' : 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]')}>{l.label}</button>
              ))}
            </div>
          </div>
          <p className="m-0 mt-1 text-[11px] text-[var(--text-muted)]">You write in English; ELVA translates for each patient.</p>

          {isText ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {[`From the Invis-Line · ${INV_NUMBERS.lineNumber}`, 'Only to patients who said yes to texts', '8:00 am – 8:00 pm', 'Counts toward the contact limit'].map((c) => (
                <span key={c} className="rounded-full border border-[var(--border-default)] bg-[var(--surface-sunken)] px-2 py-0.5 text-[11px] text-[var(--text-secondary)]">{c}</span>
              ))}
            </div>
          ) : (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted)]">Subject</div>
              <input value={subject} disabled={lang !== 'en'} onChange={(e) => { setSubject(e.target.value); }} className="mt-1 w-full rounded-[8px] border border-[var(--border-default)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)] disabled:bg-[var(--surface-sunken)]" />
            </div>
          )}

          <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted)]">{lang === 'en' ? 'Body' : `${LANGS.find((l) => l.id === lang)?.label ?? ''} — translated, read-only`}</div>
          <textarea value={body} disabled={lang !== 'en'} onChange={(e) => { setBody(e.target.value); }} className="mt-1 h-[150px] w-full resize-none rounded-[10px] border border-[var(--border-default)] px-3 py-2 text-[13px] leading-[1.5] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)] disabled:bg-[var(--surface-sunken)]" />

          {lang !== 'en' ? (
            <button type="button" className="mt-1.5 text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { toast({ title: 'Suggested change sent for review', variant: 'success' }); }}>Suggest a change</button>
          ) : !isText ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-semibold text-[var(--text-muted)]">Insert a field</span>
              {INV_PROGRAMME_FIELDS.map((f) => (
                <button key={f} type="button" onClick={() => { insertField(f); }} className="rounded-full border border-[var(--brand-indigo-line)] bg-[var(--brand-indigo-tint)] px-2 py-0.5 text-[11px] font-semibold text-[var(--brand-indigo)]">{f}</button>
              ))}
            </div>
          ) : null}

          {isText ? (
            <div className="mt-2 flex items-center justify-between text-[11px] text-[var(--text-muted)]">
              <span>“Reply STOP to opt out” is added for you</span>
              <span className={segments > 1 ? 'font-semibold text-[var(--warn-amber)]' : ''}>{chars} characters · {segments} message{segments > 1 ? 's' : ''}</span>
            </div>
          ) : null}

          {linkMissing ? (
            <div className="mt-2 flex items-center gap-2 rounded-[8px] bg-[#FBEDEC] px-3 py-2 text-[12px] text-[var(--alert-red)]"><span className="inline-block h-2 w-2 rounded-full bg-[var(--alert-red)]" /> The consult booking link is required. Add it back before you can save.</div>
          ) : null}
          {textHasMoney ? (
            <div className="mt-2 rounded-[8px] bg-[#FBEDEC] px-3 py-2 text-[12px] text-[var(--alert-red)]">Texts can’t include an amount of money.</div>
          ) : null}

          {/* Always (locked) */}
          <div className="mt-3 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
            <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-[var(--text-muted)]"><Icon kind="lock" size={11} stroke={2} /> Always in this message</div>
            <ul className="m-0 mt-1.5 list-disc pl-4 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
              {isText ? (
                <>
                  <li>Sent from the Invis-Line, {INV_NUMBERS.lineNumber}</li>
                  <li>Never an amount of money or anything from the chart</li>
                  <li>STOP stops aligner texts only</li>
                </>
              ) : (
                <>
                  <li>The consult booking link</li>
                  <li>The Invis-Line number</li>
                  <li>The aligner-only unsubscribe (added for you)</li>
                  <li>Never a case fee, “good candidate”, or a promise about results or timing</li>
                </>
              )}
            </ul>
          </div>

          {!isText ? <p className="mt-2 text-[11px] leading-[1.5] text-[var(--text-muted)]"><span className="font-semibold">Ortho benefit left</span> shows the patient’s remaining orthodontic benefit. If the plan wasn’t checked in the last 90 days, the sentence with it is left out rather than showing a stale number.</p> : null}

          {/* bottom */}
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[var(--border-default)] pt-3">
            <input value={testTo} onChange={(e) => { setTestTo(e.target.value); }} placeholder={isText ? 'A phone number' : 'An email address'} className="w-[180px] rounded-[8px] border border-[var(--border-default)] px-3 py-1.5 text-[12.5px] outline-none focus:border-[var(--brand-indigo)]" />
            <Button variant="secondary" size="sm" onClick={() => { toast({ title: `Test sent to ${testTo || 'you'}`, variant: 'success' }); }}>Send test</Button>
            <button type="button" className="text-[12px] font-semibold text-[var(--text-secondary)] hover:underline" onClick={() => { setConfirmRevert(true); }}>Go back to ELVA’s version</button>
            <Button variant="primary" size="sm" className="ml-auto" disabled={linkMissing || textHasMoney} onClick={save}>Save</Button>
          </div>
          {confirmRevert ? (
            <div className="mt-2 flex items-center gap-2 rounded-[8px] bg-[var(--surface-sunken)] px-3 py-2 text-[12px]">
              <span className="mr-auto text-[var(--text-secondary)]">Replace your version with ELVA’s?</span>
              <Button variant="secondary" size="sm" onClick={() => { setConfirmRevert(false); }}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={() => { setSubject(selected.subject ?? ''); setBody(selected.body[lang]); setConfirmRevert(false); toast({ title: 'Back to ELVA’s version', variant: 'success' }); }}>Go back</Button>
            </div>
          ) : null}
        </div>

        {/* right — preview */}
        <aside className="h-max rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
          <div className="flex items-baseline justify-between">
            <span className="pc-eyebrow text-[var(--text-muted)]">Preview for</span>
            <span className="text-[12px] font-semibold text-[var(--text-primary)]">{INV_PATIENT.name}</span>
          </div>
          {isText ? (
            <div className="mt-3 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
              <div className="text-[10.5px] font-semibold text-[var(--text-muted)]">Alamo Heights · aligner line</div>
              <div className="mt-2 rounded-[14px] rounded-bl-[4px] bg-[var(--brand-indigo)] px-3 py-2 text-[12.5px] leading-[1.5] text-white">{fill(previewBody)} Reply STOP to opt out.</div>
            </div>
          ) : (
            <div className="mt-3 rounded-[12px] border border-[var(--border-default)] p-3">
              <div className="text-[13px] font-bold text-[var(--text-primary)]">{fill(previewSubject)}</div>
              <div className="mt-2 whitespace-pre-line text-[12.5px] leading-[1.55] text-[var(--text-secondary)]">{fill(previewBody)}</div>
              <button type="button" className="mt-3 rounded-[8px] bg-[var(--brand-indigo)] px-3 py-1.5 text-[12px] font-semibold text-white">Book a consult</button>
              {selected.id === 'email-1' ? (
                <button type="button" className="mt-2 block rounded-[8px] border border-[var(--brand-indigo-line)] bg-[var(--brand-indigo-tint)] px-3 py-1.5 text-[12px] font-semibold text-[var(--brand-indigo)]">Yes, text me too</button>
              ) : null}
              <div className="mt-2 text-[10.5px] text-[var(--text-muted)]">Unsubscribe from aligner emails · you’ll still get your other messages</div>
            </div>
          )}
          <div className="mt-2 text-[11px] text-[var(--text-muted)]">{isText ? 'Sent 21 Sep · 4 replied' : 'Sent 10 Sep · opened'}</div>
        </aside>
      </div>

      {/* Invis-Line replies (INV10ProgText) */}
      <div id="replies" className="mt-6 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]" style={{ scrollMarginTop: 16 }}>
        <div className="text-[14px] font-bold text-[var(--text-primary)]">Invis-Line replies</div>
        <p className="m-0 mt-0.5 text-[12px] text-[var(--text-muted)]">Six fixed replies the line sends inside a conversation the patient started.</p>
        <div className="mt-3 grid grid-cols-2 gap-3 max-[900px]:grid-cols-1">
          {INV_LINE_REPLIES.map((r) => (
            <div key={r.id} className="rounded-[12px] border border-[var(--border-default)] p-3">
              <div className="flex items-center gap-2">
                <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{r.label}</span>
                {r.locked === true ? <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[var(--surface-sunken)] px-2 py-0.5 text-[10px] font-semibold text-[var(--text-muted)]"><Icon kind="lock" size={10} stroke={2} /> Locked</span> : <button type="button" className="ml-auto text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { toast({ title: 'Reply saved', variant: 'success' }); }}>Edit</button>}
              </div>
              <div className="mt-1.5 text-[12px] leading-[1.5] text-[var(--text-secondary)]">{r.body}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The {Smile profile line} per profile (spec 35 §3). */
const SMILE_LINES: Record<string, string> = {
  'crooked teeth': 'When teeth are crowded, they overlap or twist because there isn’t quite enough room.',
  'gap teeth': 'Gaps between teeth are one of the most common things people ask us about.',
  overbite: 'An overbite is when the top teeth sit well in front of the bottom ones.',
  underbite: 'An underbite is when the bottom teeth sit in front of the top ones.',
  crossbite: 'A crossbite is when some top teeth sit inside the bottom ones when you bite.',
  'open bite': 'An open bite is when some top and bottom teeth don’t meet when you close.',
};

/**
 * Fill the field tokens with the preview patient's real details (spec 35). The
 * sentence-valued fields — the benefit sentence, the provider sentence, the
 * smile-profile line — render in full for Orville (Delta Dental $1,500, Gold,
 * crowding); the top-concern sentence is left out when it isn't an event.
 */
function fill(s: string): string {
  const smile = (INV_PATIENT.smileProfile[0] ?? 'Crooked teeth').toLowerCase();
  const smileLine = SMILE_LINES[smile] ?? 'Clear aligners are a common, low-fuss way to straighten things out.';
  return s
    .replace(/\{First name\}/g, INV_PATIENT.first)
    .replace(/\{Smile profile line\}/g, smileLine)
    .replace(/\{Smile profile\}/g, smile)
    .replace(/\{Ortho benefit left\}/g, 'Your Delta Dental plan includes $1,500 of orthodontic benefit, and you haven’t used any of it.')
    .replace(/\{Provider level\}/g, 'Dr. Carter is a Gold Invisalign provider.')
    .replace(/\{Top concern sentence\}/g, '')
    .replace(/\{Consult booking link\}/g, 'alamoheights.elva.ai/book/aligners')
    .replace(/\{Practice name\}/g, 'Alamo Heights Dental')
    .replace(/\{Doctor\}/g, 'Dr. Carter')
    .replace(/\{Top concern\}/g, INV_PATIENT.concerns[0] ?? 'cost')
    // Tidy the blank line a left-out sentence leaves behind.
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
